/* ───────────────────────────────  PLURALS  ──────────────────────────────────
 *
 * QA, 2026-08-27: "loses access to 1 building(s)". Also "1 entries", and
 * "guest(s)/people".
 *
 * The product already gets this right in most places — dashboard-scopes.tsx
 * writes `count === 1 ? 'grant' : 'grants'` inline, twice — so this is not a
 * missing capability, it is the same three-line ternary written out at some
 * call sites and shortened to a "(s)" at others. "(s)" is a note to a
 * translator, not a sentence a product says to a person; it reads as
 * unfinished, and on a DESTRUCTIVE confirmation ("loses access to 1
 * building(s) immediately") unfinished reads as untrustworthy.
 *
 * One helper, so a fourth call site has something to reach for other than
 * inventing a fifth spelling.
 *
 * Deliberately NOT an Intl.PluralRules wrapper. This product's copy is English
 * only; PluralRules would buy correct Polish and cost every caller a locale
 * argument it has nothing to pass. If Lobbero is ever translated, this is the
 * one function that changes.
 */

/**
 * The noun alone, agreeing with `count`.
 *
 *   plural(1, 'building')            -> 'building'
 *   plural(3, 'building')            -> 'buildings'
 *   plural(0, 'building')            -> 'buildings'   (English: "0 buildings")
 *   plural(2, 'entry', 'entries')    -> 'entries'
 *
 * `plural` is the irregular form; without it an 's' is appended, which is right
 * for every regular noun this product uses and wrong silently for the ones it
 * is not — hence the explicit parameter rather than a guess at -y/-s/-ch rules.
 */
export const plural = (count: number, singular: string, pluralForm?: string): string =>
  Math.abs(count) === 1 ? singular : pluralForm ?? `${singular}s`;

/**
 * The count AND the noun, which is what almost every call site actually wants.
 *
 *   countOf(1, 'building')          -> '1 building'
 *   countOf(3, 'building')          -> '3 buildings'
 *   countOf(1, 'entry', 'entries')  -> '1 entry'
 *
 * The number is formatted with toLocaleString so four-figure counts get their
 * separator, matching how figures are rendered elsewhere in the dashboard.
 */
export const countOf = (count: number, singular: string, pluralForm?: string): string =>
  `${count.toLocaleString()} ${plural(count, singular, pluralForm)}`;
